import React, { useEffect, useState } from "react";
import moment from "moment"; // Make sure you import moment if you're using it
import { getImage, REPORT_ROUTES } from "../../utils/utility-functions";
import Loader from "../../components/loader";
import {
  BIG_FIVE_TEST_ASSESSMENT_ID,
  CAREER_TEST_ASSESSMENT_ID,
  PERSONALITY_TEST_ASSESSMENT_ID,
} from "../../utils/general-config";
import { useNavigate, useParams } from "react-router-dom";
import { ROUTE_CONSTANT } from "../../routing/route-constants";
import ViewReportIcon from "../../assets/dasboard/view-results-image.svg";
import AppPopOver from "../../components/popover/component";
import { toast } from "react-toastify";
import DownloadReportIcon from "../../assets/dasboard/download-report-image.svg";
import PathHelper from "../../utils/path-helper";
import { LOCALE } from "../../utils/constants";

function AdminUserDocuments({
  getAssessmentAttemptByUserIdConnect,
  funnelStep,
  GetUserByIdConnect,
  IsGuestOrPremiumUserConnect,
  DownloadAssessmentResultConnect,
}) {
  const { locale } = useParams();
  const currentLocale = locale ?? LOCALE.EN;
  const { userIdSearchByAdmin } = funnelStep;
  const [assessmentAttemptData, setAssessmentAttemptData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [userData, setUserData] = useState({});
  const [popoverOpen, setPopoverOpen] = React.useState({});
  const [openNewTest, setOpenNewTest] = React.useState(false);
  const [testPanelOpen, setTestPanelOpen] = useState(null);
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [isPremiumOrGuest, setIsPremiumOrGuest] = React.useState(false);
  const handlePopoverClick = (index) => {
    setPopoverOpen((prevOpen) => (prevOpen === index ? null : index));
    setOpenNewTest(false);
  };
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      if (!userIdSearchByAdmin) return; // Exit if userGUID is not available

      setIsLoading(true);

      try {
        // 1. Fetch Assessment Attempt Data
        const fetchedData =
          await getAssessmentAttemptByUserIdConnect(userIdSearchByAdmin);
        setAssessmentAttemptData(fetchedData);
        // 1. Fetch User Data

        const fetchUserData = await GetUserByIdConnect(userIdSearchByAdmin);
        setUserData(fetchUserData);
        const isPremiumOrGuestUser =
          await IsGuestOrPremiumUserConnect(userIdSearchByAdmin);
        setIsPremiumOrGuest(isPremiumOrGuestUser);
      } catch (error) {
        console.log(error); // Log errors from any of the fetch operations
      } finally {
        setIsLoading(false); // Stop loading state only after all operations complete
      }
    };
    fetchData();
  }, [userIdSearchByAdmin, getAssessmentAttemptByUserIdConnect]);

  const assessmentData =
    assessmentAttemptData?.length > 0
      ? assessmentAttemptData
          ?.map((item) => {
            return {
              assessmentId: item?.assessmentId,
              userId: item?.userGuid,
              title: item?.assessmentTitle,
              date: item?.createdOn,
              assessmentAttemptId: item?.assessmentAttemptId,
              personality: item?.personality,
              createdOn: item?.createdOn,
            };
          })
          .sort((a, b) => new Date(b.createdOn) - new Date(a.createdOn))
      : [];

  const handleViewReport = (
    isPremiumOrGuest,
    assessmentAttemptId,
    assessmentId,
    userId
  ) => {
    const reportRoute = REPORT_ROUTES[assessmentId]; // Dynamically get the route for the given assessmentId
    if (!reportRoute) {
      console.error(`No route found for assessmentId: ${assessmentId}`);
      return;
    }
    if (isPremiumOrGuest) {
      const path = PathHelper(reportRoute, currentLocale);
      navigate(
        `${path.next}?aai=${assessmentAttemptId}&uid=${userIdSearchByAdmin}`
      ); // for example
    } else {
      navigate(ROUTE_CONSTANT.PAYMENT_PLAN);
    }
  };

  const handleDownloadReport = async (
    isPremiumOrGuest,
    assessmentAttemptId
  ) => {
    if (isPremiumOrGuest) {
      setIsButtonLoading(true);
      setPopoverOpen({});
      try {
        const res = await DownloadAssessmentResultConnect(
          userIdSearchByAdmin,
          assessmentAttemptId
        );
        if (res && res.hrefBlobUrl) {
          window.open(res.hrefBlobUrl, "_blank");
        } else {
          toast.error("Failed to download assessment result.");
        }
      } catch (error) {
        toast.error(
          "An error occurred while downloading the assessment result."
        );
      } finally {
        setIsButtonLoading(false);
      }
    } else {
      navigate(ROUTE_CONSTANT.PAYMENT_PLAN);
    }
  };

  const getMobileActions = () => {
    const viewReportAction = {
      label: "View Report",
      icon: ViewReportIcon,
      onClick: () => {
        handleViewReport(
          isPremiumOrGuest,
          assessmentData[testPanelOpen]?.assessmentAttemptId,
          assessmentData[testPanelOpen]?.assessmentId
        );
        setTestPanelOpen(null);
      },
    };

    const downloadReportAction = {
      label: "Download Report",
      icon: DownloadReportIcon,
      onClick: () => {
        handleDownloadReport(
          isPremiumOrGuest,
          assessmentData[testPanelOpen]?.assessmentAttemptId
        );
        setTestPanelOpen(null);
      },
    };

    const actionsByAssessmentId = {
      [PERSONALITY_TEST_ASSESSMENT_ID]: [
        viewReportAction,
        downloadReportAction,
      ],
      [CAREER_TEST_ASSESSMENT_ID]: [viewReportAction],
      [BIG_FIVE_TEST_ASSESSMENT_ID]: [viewReportAction],
    };

    return (
      actionsByAssessmentId[assessmentData[testPanelOpen]?.assessmentId] || [
        viewReportAction,
      ]
    );
  };

  return (
    <div>
      {" "}
      <Loader isLoading={isLoading || isButtonLoading} />
      <div className="max-w-full w-full bg-transparent  md:px-0 overflow-y-auto">
        <div className="flex justify-center relative shadow-md rounded-[20px] w-full mx-auto items-center ">
          <table className="h-[200px] md:h-[920px] overflow-y-auto w-full text-[16px] text-left rtl:text-right rounded-[20px]  text-gray-500 dark:text-gray-400 lg:mx-14">
            <thead className="rounded-[20px] text-[16px] text-insightful-gray uppercase bg-insightful-lightest-gray hover:bg-insightful-light-gray dark:bg-gray-700 relative dark:text-gray-400">
              {/* Add table headers here */}
            </thead>
            <tbody>
              {assessmentData?.length > 0 ? (
                assessmentData.map((item, index) => {
                  const pGroup =
                    item?.personality &&
                    item?.personality !== null &&
                    item?.personality?.role.toLowerCase();

                  const pName =
                    item?.personality &&
                    item?.personality !== null &&
                    item?.personality?.name.toLowerCase();

                  const status =
                    item?.personality && item?.personality !== null
                      ? "Completed"
                      : "In Progress";

                  const TEST_TYPES = {
                    [PERSONALITY_TEST_ASSESSMENT_ID]: "PersonalityTest",
                    [BIG_FIVE_TEST_ASSESSMENT_ID]: "BigFiveTest",
                    [CAREER_TEST_ASSESSMENT_ID]: "CareerTest",
                  };

                  const testType =
                    TEST_TYPES[item?.assessmentId] || "UnknownTest";

                  const getContent = () => {
                    const commonActions = [
                      {
                        label: "View report",
                        onClick: () => {
                          handleViewReport(
                            isPremiumOrGuest,
                            item?.assessmentAttemptId,
                            item?.assessmentId,
                            item?.userId
                          );
                        },
                        icon: ViewReportIcon,
                        className: "text-red-500",
                      },
                    ];

                    const specificActions =
                      testType === "PersonalityTest"
                        ? [
                            {
                              label: "Download Report",
                              onClick: () => {
                                handleDownloadReport(
                                  isPremiumOrGuest,
                                  item.assessmentAttemptId,
                                  item.userId
                                );
                              },
                              icon: DownloadReportIcon,
                            },
                          ]
                        : [];

                    return [...commonActions, ...specificActions];
                  };

                  return (
                    <React.Fragment key={index}>
                      <tr className="flex justify-between max-h-max h-[100px] p-2 w-full mx-0 first:rounded-t-[20px] last:rounded-b-[20px] bg-insightful-lightest-gray hover:bg-insightful-light-gray border-b last:border-b-0 border-insightful-light-gray dark:bg-gray-800 dark:border-gray-700 items-center">
                        <td className="min-w-max w-[50px] h-auto p-4 rounded-full overflow-hidden">
                          <img
                            src={getImage(
                              pGroup,
                              pName,
                              true,
                              userData?.gender
                            )}
                            alt="Profile"
                            className="object-cover rounded-full transition-transform transform scale-110 w-16 h-16"
                          />
                        </td>
                        <td className="flex flex-col justify-center px-4 py-4 md:px-10 md:py-6 w-full">
                          <p className="text-gray-500 font-bold text-sm sm:text-base">
                            {item?.title}
                          </p>

                          <p className="text-xs sm:text-sm text-green-600">
                            {status}
                          </p>
                          <p className="block md:hidden text-gray-500 text-xs sm:text-sm">
                            {moment(item?.createdOn).format("MM/DD/YYYY")}
                          </p>
                        </td>
                        <td className="hidden md:table-cell px-4 py-4 sm:px-6 sm:py-8 text-right">
                          <p className="text-gray-500 text-xs sm:text-sm">
                            {moment(item?.createdOn).format("MM/DD/YYYY")}
                          </p>
                        </td>
                        <td className="px-4 py-4 text-right flex md:table-cell relative rounded-full items-center">
                          {status === "Completed" && (
                            <button
                              id={`three-dots-button-${item?.title || index}`}
                              className="text-insightful-gray hover:text-insightful-black text-4xl font-bold text-left"
                              onClick={() => {
                                if (window.innerWidth >= 768) {
                                  handlePopoverClick(index);
                                } else {
                                  setTestPanelOpen(index);
                                }
                              }}
                            >
                              &#x22EE;
                            </button>
                          )}

                          {popoverOpen === index && (
                            <div className="hidden md:block">
                              <AppPopOver
                                className="text-left font-semibold text-xs bg-white"
                                classNameList="text-[14px] text-left"
                                isPopoverOpen={popoverOpen === index}
                                setIsPopoverOpen={setPopoverOpen}
                                content={getContent()}
                              />
                            </div>
                          )}
                          {testPanelOpen !== null && (
                            <>
                              <div className="md:hidden max-h-max">
                                <div
                                  className={`fixed inset-x-0 bottom-0 max-h-max bg-white rounded-t-[20px] p-4 z-[1000] transform transition-transform duration-300 ease-in-out ${
                                    testPanelOpen !== null
                                      ? "translate-y-0"
                                      : "translate-y-full"
                                  }`}
                                >
                                  <div className="flex justify-between items-center mb-4">
                                    <div className="text-left items-center px-2">
                                      <h2 className="text-xl font-bold">
                                        {assessmentData[testPanelOpen]?.title}
                                      </h2>
                                      <p className="text-xs text-gray-500 ml-1">
                                        {moment(
                                          assessmentData[testPanelOpen]
                                            ?.createdOn
                                        ).format("MM/DD/YYYY")}
                                        {assessmentData[testPanelOpen]
                                          ?.personality ? (
                                          <span className="text-xs ml-2 text-green-500">
                                            Completed
                                          </span>
                                        ) : (
                                          <span className="text-xs ml-2 text-insightful-orange">
                                            In Progress
                                          </span>
                                        )}
                                      </p>
                                    </div>
                                    <button
                                      id="mobile-test-panel-button"
                                      onClick={() => setTestPanelOpen(null)}
                                      className="flex justify-center items-center"
                                    >
                                      Close
                                      <span className="text-[24px] ml-1">
                                        &times;
                                      </span>
                                    </button>
                                  </div>
                                  <div className="space-y-[11px]">
                                    {getMobileActions().map((item, index) =>
                                      item.type === "divider" ? (
                                        <hr
                                          key={index}
                                          className="border-t border-gray-200"
                                        />
                                      ) : (
                                        <button
                                          key={index}
                                          className={`flex items-center w-full p-2 hover:bg-gray-100 rounded ${
                                            item.className || ""
                                          }`}
                                          onClick={item.onClick}
                                        >
                                          <img
                                            src={item.icon}
                                            alt={item.label}
                                            className="w-6 h-6 mr-2"
                                          />
                                          <span>{item.label}</span>
                                        </button>
                                      )
                                    )}
                                  </div>
                                </div>

                                {/* Overlay to close the panel when clicking outside */}
                                {/* {testPanelOpen !== null && (
                                  <div
                                    className="fixed inset-0 bg-black bg-opacity-50 z-30"
                                    onClick={() => setTestPanelOpen(null)}
                                  ></div>
                                )} */}
                              </div>
                            </>
                          )}
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="4" className="text-center py-4">
                    No assessment data found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default AdminUserDocuments;
